/* eslint-disable */
import React, { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Slider,
  Card,
  Row,
  Col,
  Typography,
  Space,
  message,
  Divider,
} from 'antd'
import {
  DownloadOutlined,
  EyeOutlined,
  UndoOutlined,
  SaveOutlined,
  PlusOutlined,
  MinusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import {
  CertificateConfig,
  CertificateTemplate,
  DEFAULT_CERTIFICATE_TEMPLATE,
  downloadGeneratedCertificate,
  previewCertificate,
  getCertificateAsBase64,
} from '../../../utils/certificateGenerator'

const { Title, Text } = Typography

interface CertificatePositionEditorProps {
  studentName?: string
  onSave?: (config: CertificateConfig) => void
  template?: CertificateTemplate
}

const CertificatePositionEditor: React.FC<CertificatePositionEditorProps> = ({
  studentName = 'John Doe',
  onSave,
  template = DEFAULT_CERTIFICATE_TEMPLATE,
}) => {
  const [config, setConfig] = useState<CertificateConfig>({
    name: studentName,
    x: template.namePosition.x, // Will be undefined if not set, causing auto-centering
    y: template.namePosition.y,
    courseName: 'React Development Fundamentals',
    courseX: template.coursePosition?.x, // Will be undefined if not set, causing auto-centering
    courseY: template.coursePosition?.y || 350,
    fontSize: template.defaultFontSize || 24,
    fontColor: template.defaultFontColor || { r: 42, g: 170, b: 250 },
    courseFontSize: template.defaultCourseFontSize || 18,
    courseFontColor: template.defaultCourseFontColor || { r: 51, g: 51, b: 51 },
    enableTextWrapping: true,
    maxTextWidth: 80,
    lineHeight: 1.3,
  })

  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  // Generate initial preview when component mounts
  useEffect(() => {
    generatePreview()
  }, []) // Empty dependency array - only run once on mount

  // Generate preview when config changes (debounced)
  useEffect(() => {
    // Debounce the preview generation to avoid too many API calls
    const timeoutId = setTimeout(() => {
      generatePreview()
    }, 1000) // Increased debounce time

    return () => clearTimeout(timeoutId)
  }, [config.name, config.x, config.y, config.fontSize, config.fontColor])

  const generatePreview = async () => {
    try {
      setLoading(true)
      console.log('Generating preview with config:', config)
      console.log('Using template:', template)

      // Test if template is accessible
      try {
        const response = await fetch(template.templateUrl)
        console.log('Template fetch response:', response.status, response.ok)
        if (!response.ok) {
          throw new Error(`Template not accessible: ${response.status}`)
        }
      } catch (fetchError) {
        console.error('Template fetch error:', fetchError)
        throw new Error(`Template tidak dapat diakses: ${fetchError}`)
      }

      const base64Url = await getCertificateAsBase64(config, template)
      console.log('Preview URL generated:', base64Url.substring(0, 100) + '...')
      setPreviewUrl(base64Url)
      console.log('Preview generated successfully')
    } catch (error) {
      message.error('Gagal generate preview sertifikat')
      console.error('Preview generation error:', error)

      // Show more specific error message
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          message.error(
            'Template PDF tidak ditemukan. Pastikan file template ada di public/assets/certificates/',
          )
        } else if (error.message.includes('PDF')) {
          message.error('Error dalam memproses PDF template')
        } else {
          message.error(`Error: ${error.message}`)
        }
      }

      setPreviewUrl('') // Clear preview on error
    } finally {
      setLoading(false)
    }
  }

  const handleGeneratePreview = async () => {
    await generatePreview()
  }

  const handleDownload = async () => {
    try {
      setLoading(true)
      await downloadGeneratedCertificate(config, template)
      message.success('Sertifikat berhasil diunduh')
    } catch (error) {
      message.error('Gagal mengunduh sertifikat')
      console.error('Download error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePreview = async () => {
    try {
      setLoading(true)
      await previewCertificate(config, template)
      message.success('Preview sertifikat dibuka di tab baru')
    } catch (error) {
      message.error('Gagal membuka preview sertifikat')
      console.error('Preview error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = () => {
    if (onSave) {
      onSave(config)
      message.success('Konfigurasi posisi tersimpan')
    }
  }

  const handleReset = () => {
    setConfig({
      name: studentName,
      x: template.namePosition.x, // Will be undefined if not set, causing auto-centering
      y: template.namePosition.y,
      courseName: 'React Development Fundamentals',
      courseX: template.coursePosition?.x, // Will be undefined if not set, causing auto-centering
      courseY: template.coursePosition?.y || 360,
      fontSize: template.defaultFontSize || 24,
      fontColor: template.defaultFontColor || { r: 42, g: 170, b: 250 },
      courseFontSize: template.defaultCourseFontSize || 18,
      courseFontColor: template.defaultCourseFontColor || {
        r: 51,
        g: 51,
        b: 51,
      },
    })
    message.info('Posisi direset ke default')
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    // Check if click is near the name position
    const nameX = (config.x / 595) * rect.width // A4 width = 595pt
    const nameY = (config.y / 842) * rect.height // A4 height = 842pt

    const distance = Math.sqrt((x - nameX) ** 2 + (y - nameY) ** 2)

    if (distance < 50) {
      // 50px tolerance
      setIsDragging(true)
      setDragOffset({ x: x - nameX, y: y - nameY })
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left - dragOffset.x
    const y = e.clientY - rect.top - dragOffset.y

    // Convert back to PDF coordinates
    const pdfX = (x / rect.width) * 595
    const pdfY = (y / rect.height) * 842

    setConfig((prev) => ({
      ...prev,
      x: Math.max(0, Math.min(595, pdfX)),
      y: Math.max(0, Math.min(842, pdfY)),
    }))
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  return (
    <div style={{ padding: '20px' }}>
      <Title level={3}>Editor Posisi Sertifikat</Title>

      <Row gutter={[24, 24]}>
        {/* Controls Panel */}
        <Col xs={24} lg={8}>
          <Card title="Pengaturan Posisi" style={{ height: 'fit-content' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Nama:</Text>
                <Input
                  value={config.name}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Masukkan nama"
                  style={{ marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>Nama Course:</Text>
                <Input
                  value={config.courseName || ''}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      courseName: e.target.value,
                    }))
                  }
                  placeholder="Masukkan nama course"
                  style={{ marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>
                  Posisi X:{' '}
                  {config.x !== undefined
                    ? `${Math.round(config.x)}pt`
                    : 'Auto (Tengah)'}
                </Text>
                <Slider
                  min={0}
                  max={595}
                  value={config.x || 297.5}
                  onChange={(value) =>
                    setConfig((prev) => ({ ...prev, x: value }))
                  }
                  style={{ marginTop: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  Kosongkan untuk auto-centering berdasarkan panjang nama
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type={config.x === undefined ? 'primary' : 'default'}
                    onClick={() =>
                      setConfig((prev) => ({ ...prev, x: undefined }))
                    }
                    style={{ marginRight: 8 }}
                  >
                    Auto Center
                  </Button>
                  <Button
                    size="small"
                    type={config.x !== undefined ? 'primary' : 'default'}
                    onClick={() => setConfig((prev) => ({ ...prev, x: 297.5 }))}
                  >
                    Manual Position
                  </Button>
                </div>
              </div>

              <div>
                <Text strong>Posisi Y: {Math.round(config.y)}pt</Text>
                <Slider
                  min={0}
                  max={842}
                  value={config.y}
                  onChange={(value) =>
                    setConfig((prev) => ({ ...prev, y: value }))
                  }
                  style={{ marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>Ukuran Font: {config.fontSize}pt</Text>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <Button
                    icon={<MinusOutlined />}
                    onClick={() =>
                      setConfig((prev) => ({
                        ...prev,
                        fontSize: Math.max(8, (prev.fontSize || 24) - 2),
                      }))
                    }
                  />
                  <Slider
                    min={8}
                    max={72}
                    value={config.fontSize}
                    onChange={(value) =>
                      setConfig((prev) => ({ ...prev, fontSize: value }))
                    }
                    style={{ flex: 1 }}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setConfig((prev) => ({
                        ...prev,
                        fontSize: Math.min(72, (prev.fontSize || 24) + 2),
                      }))
                    }
                  />
                </div>
              </div>

              <div>
                <Text strong>Warna Font:</Text>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Input
                    type="color"
                    value={`#${config.fontColor.r
                      .toString(16)
                      .padStart(2, '0')}${config.fontColor.g
                      .toString(16)
                      .padStart(2, '0')}${config.fontColor.b
                      .toString(16)
                      .padStart(2, '0')}`}
                    onChange={(e) => {
                      const hex = e.target.value.replace('#', '')
                      const r = parseInt(hex.substr(0, 2), 16)
                      const g = parseInt(hex.substr(2, 2), 16)
                      const b = parseInt(hex.substr(4, 2), 16)
                      setConfig((prev) => ({ ...prev, fontColor: { r, g, b } }))
                    }}
                    style={{ width: 60 }}
                  />
                  <Text code>
                    RGB({config.fontColor.r}, {config.fontColor.g},{' '}
                    {config.fontColor.b})
                  </Text>
                </div>
              </div>

              {/* Course Settings */}
              <Divider>Pengaturan Course</Divider>

              <div>
                <Text strong>
                  Posisi X Course:{' '}
                  {config.courseX !== undefined
                    ? `${Math.round(config.courseX)}pt`
                    : 'Auto (Tengah)'}
                </Text>
                <Slider
                  min={0}
                  max={595}
                  value={config.courseX || 297.5}
                  onChange={(value) =>
                    setConfig((prev) => ({ ...prev, courseX: value }))
                  }
                  style={{ marginTop: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  Kosongkan untuk auto-centering berdasarkan panjang course
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type={config.courseX === undefined ? 'primary' : 'default'}
                    onClick={() =>
                      setConfig((prev) => ({ ...prev, courseX: undefined }))
                    }
                    style={{ marginRight: 8 }}
                  >
                    Auto Center
                  </Button>
                  <Button
                    size="small"
                    type={config.courseX !== undefined ? 'primary' : 'default'}
                    onClick={() =>
                      setConfig((prev) => ({ ...prev, courseX: 297.5 }))
                    }
                  >
                    Manual Position
                  </Button>
                </div>
              </div>

              <div>
                <Text strong>
                  Posisi Y Course: {Math.round(config.courseY || 320)}pt
                </Text>
                <Slider
                  min={0}
                  max={842}
                  value={config.courseY || 320}
                  onChange={(value) =>
                    setConfig((prev) => ({ ...prev, courseY: value }))
                  }
                  style={{ marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>
                  Ukuran Font Course: {config.courseFontSize || 18}pt
                </Text>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <Button
                    icon={<MinusOutlined />}
                    onClick={() =>
                      setConfig((prev) => ({
                        ...prev,
                        courseFontSize: Math.max(
                          8,
                          (prev.courseFontSize || 18) - 2,
                        ),
                      }))
                    }
                  />
                  <Slider
                    min={8}
                    max={72}
                    value={config.courseFontSize || 18}
                    onChange={(value) =>
                      setConfig((prev) => ({ ...prev, courseFontSize: value }))
                    }
                    style={{ flex: 1 }}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setConfig((prev) => ({
                        ...prev,
                        courseFontSize: Math.min(
                          72,
                          (prev.courseFontSize || 18) + 2,
                        ),
                      }))
                    }
                  />
                </div>
              </div>

              <div>
                <Text strong>Warna Font Course:</Text>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Input
                    type="color"
                    value={`#${(config.courseFontColor?.r || 51)
                      .toString(16)
                      .padStart(2, '0')}${(config.courseFontColor?.g || 51)
                      .toString(16)
                      .padStart(2, '0')}${(config.courseFontColor?.b || 51)
                      .toString(16)
                      .padStart(2, '0')}`}
                    onChange={(e) => {
                      const hex = e.target.value.replace('#', '')
                      const r = parseInt(hex.substr(0, 2), 16)
                      const g = parseInt(hex.substr(2, 2), 16)
                      const b = parseInt(hex.substr(4, 2), 16)
                      setConfig((prev) => ({
                        ...prev,
                        courseFontColor: { r, g, b },
                      }))
                    }}
                    style={{ width: 60 }}
                  />
                  <Text code>
                    RGB({config.courseFontColor?.r || 51},{' '}
                    {config.courseFontColor?.g || 51},{' '}
                    {config.courseFontColor?.b || 51})
                  </Text>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={loading}
                >
                  Simpan
                </Button>
                <Button icon={<UndoOutlined />} onClick={handleReset}>
                  Reset
                </Button>
              </div>

              {/* Text Wrapping Settings */}
              <Divider>Pengaturan Text Wrapping</Divider>

              <div>
                <Text strong>Enable Text Wrapping:</Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type={config.enableTextWrapping ? 'primary' : 'default'}
                    onClick={() =>
                      setConfig((prev) => ({
                        ...prev,
                        enableTextWrapping: true,
                      }))
                    }
                    style={{ marginRight: 8 }}
                  >
                    Aktif
                  </Button>
                  <Button
                    size="small"
                    type={!config.enableTextWrapping ? 'primary' : 'default'}
                    onClick={() =>
                      setConfig((prev) => ({
                        ...prev,
                        enableTextWrapping: false,
                      }))
                    }
                  >
                    Nonaktif
                  </Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  Aktifkan untuk auto-break text yang terlalu panjang
                </Text>
              </div>

              {config.enableTextWrapping && (
                <>
                  <div>
                    <Text strong>Max Text Width: {config.maxTextWidth}%</Text>
                    <Slider
                      min={50}
                      max={95}
                      value={config.maxTextWidth || 80}
                      onChange={(value) =>
                        setConfig((prev) => ({ ...prev, maxTextWidth: value }))
                      }
                      style={{ marginTop: 8 }}
                    />
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, marginTop: 4 }}
                    >
                      Lebar maksimal text dalam persentase dari lebar halaman
                    </Text>
                  </div>

                  <div>
                    <Text strong>Line Height: {config.lineHeight}</Text>
                    <Slider
                      min={1.0}
                      max={2.0}
                      step={0.1}
                      value={config.lineHeight || 1.3}
                      onChange={(value) =>
                        setConfig((prev) => ({ ...prev, lineHeight: value }))
                      }
                      style={{ marginTop: 8 }}
                    />
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, marginTop: 4 }}
                    >
                      Jarak antar baris (multiplier dari ukuran font)
                    </Text>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleGeneratePreview}
                  loading={loading}
                >
                  Generate Preview
                </Button>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  loading={loading}
                >
                  Preview di Tab
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  loading={loading}
                >
                  Download
                </Button>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Preview Panel */}
        <Col xs={24} lg={16}>
          <Card title="Preview Sertifikat" style={{ height: 'fit-content' }}>
            <div
              style={{
                position: 'relative',
                border: '1px solid #d9d9d9',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '600px',
                    border: 'none',
                    pointerEvents: isDragging ? 'none' : 'auto',
                  }}
                  title="Certificate Preview"
                  onLoad={() => console.log('Preview iframe loaded')}
                  onError={(e) => console.error('Preview iframe error:', e)}
                />
              ) : (
                <div
                  style={{
                    height: '600px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    flexDirection: 'column',
                    gap: 16,
                  }}
                >
                  {loading ? (
                    <div style={{ textAlign: 'center' }}>
                      <Text>Loading preview...</Text>
                      <br />
                      <Text type="secondary">Sedang memproses sertifikat</Text>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <Text type="secondary">
                        Preview sertifikat belum di-generate
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Klik "Generate Preview" untuk melihat hasil
                      </Text>
                    </div>
                  )}
                </div>
              )}

              {/* Position indicator */}
              {previewUrl && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${(config.x / 595) * 100}%`,
                    top: `${(config.y / 842) * 100}%`,
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'red',
                    borderRadius: '50%',
                    border: '2px solid white',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 10,
                  }}
                />
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                💡 Tip: Klik dan drag pada area sertifikat untuk mengatur posisi
                nama secara visual
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default CertificatePositionEditor
